import type {
  MeldunekResponseDTO,
  NewProductionResponse,
  ProductionOrderResponseDTO,
  ProductResponceDTO,
  TechnologyResponseDTO,
} from "../../../client"
import type { RuntimeCompanyProfile } from "../../../utils/companyProfileStorage"
import {
  escapeHtml,
  formatNumber,
  generateGenericLabelHtml,
  generateSimpleLabelHtml as generateSimpleBaseLabelHtml,
  getLabelStyles,
  getSimpleLabelStyles,
} from "../sharedLabelUtils"

export const generateLabelHtml = (
  order: ProductionOrderResponseDTO,
  meldunek: MeldunekResponseDTO,
  outputProduct: ProductResponceDTO | null | undefined,
  technology: TechnologyResponseDTO | null | undefined,
  activeProduction: NewProductionResponse | null | undefined,
  companyProfile?: Pick<
    RuntimeCompanyProfile,
    | "shortName"
    | "legalName"
    | "taxId"
    | "regon"
    | "krs"
    | "email"
    | "phone"
    | "website"
    | "assets"
    | "alt"
    | "address"
  >,
) => {
  const techLabel = technology
    ? `${technology.code ?? ""} ${technology.name ?? ""}`.trim()
    : ""
  const productName = outputProduct?.name ?? meldunek.output_product_id
  const meldunekBarcode = meldunek.number || meldunek.id

  const fmtDateOnly = (value?: string | null) =>
    value ? new Date(value).toISOString().split("T")[0] : "—"
  const fmtTimeOnly = (value?: string | null) =>
    value ? new Date(value).toLocaleTimeString() : "—"

  const prodData = activeProduction?.new_production?.data
  const productColor =
    outputProduct?.extra_data?.color ??
    outputProduct?.extra_data?.kolor ??
    outputProduct?.extra_data?.barva
  const color = productColor || prodData?.header.color || "—"

  const productThread = outputProduct?.extra_data?.gwint
  const thread = productThread || prodData?.header.thread || "—"

  const productWeight =
    outputProduct?.extra_data?.weight ??
    outputProduct?.extra_data?.weight_grams ??
    outputProduct?.extra_data?.waga
  const weight =
    (productWeight !== undefined && productWeight !== null
      ? String(productWeight)
      : null) ||
    prodData?.header.weight_grams ||
    "—"
  const capacity = outputProduct?.name.match(/(\d+)\s*ml/i)?.[1] ?? "—"

  return generateGenericLabelHtml({
    title: "Meldunek Produkcyjny",
    productName: productName,
    sku: outputProduct?.sku ?? "—",
    barcodeValue: meldunekBarcode,
    secondaryTitle: techLabel,
    specs: [
      { label: "Kolor", value: color },
      { label: "Rodzaj gwintu", value: thread },
      { label: "Gramatura [g]", value: weight },
      { label: "Pojemność [ml]", value: capacity },
      {
        label: "Data / Zlecenie",
        value: `${fmtDateOnly(meldunek.created_at)} ${fmtTimeOnly(
          meldunek.created_at,
        )} | ${order.number ?? "—"}`,
      },
    ],
    detailsLeft: [
      "Indeks kontrahenta: —",
      `Lokalizacja wyrobu: ${meldunek.output_location_code || "—"}`,
    ],
    detailsCenter: {
      label: "Nr Palety",
      value: String(meldunek.seq_no),
    },
    detailsRight: [
      "Waga netto: —",
      `Nr partii/Nr zlecenia: ${order.number ?? "—"}`,
      `Lokalizacje materiałów: ${
        (meldunek.items ?? [])
          .map((item) => item.location_code)
          .filter(Boolean)
          .join(", ") || "—"
      }`,
    ],
    companyProfile,
  })
}

export const generateSimpleMeldunekLabelHtml = (
  meldunek: MeldunekResponseDTO,
  outputProduct: ProductResponceDTO | null | undefined,
) => {
  const productName = outputProduct?.name ?? meldunek.output_product_id
  const meldunekBarcode = meldunek.number || meldunek.id
  return generateSimpleBaseLabelHtml({
    productName,
    barcodeValue: meldunekBarcode,
  })
}

export { escapeHtml, formatNumber, getLabelStyles, getSimpleLabelStyles }
